import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// ─── Org settings (key/value JSONB) ─────────────────────────────────────────
// Shared with the boot prefetch (lib/route-preload), which starts this request
// alongside the session check instead of after the shell has rendered.
export const orgSettingsQuery = {
  queryKey: ['org-settings'],
  queryFn: async () => {
    const { data } = await api.get('/api/settings');
    return data; // { values: {key: val}, settings: [...rows] }
  },
  staleTime: 30_000,
};

export function useOrgSettings() {
  return useQuery(orgSettingsQuery);
}

export function useUpdateOrgSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch) => {
      // patch is a flat { 'key': value } map
      const { data } = await api.patch('/api/settings', patch);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-settings'] }),
  });
}

// Whether scheduling features (Calendar, Meetings, Today's calls, booking forms)
// are shown for this workspace. Lead-generation-only tenants turn this OFF to
// declutter. It is a soft display preference - nothing is disabled or deleted,
// and it can be flipped back any time. Absent / not-false => ON (the default),
// so existing workspaces are unaffected until someone opts out.
//   { enabled: bool, isLoading: bool }
export function useScheduling() {
  const { data, isLoading } = useOrgSettings();
  const flag = data?.values?.['features.scheduling'];
  return { enabled: flag !== false, isLoading };
}

// Whether a billing provider (Stripe or SamCart) is connected. Decides whether
// the payment-failed and subscription triggers are offered - they can only
// ever fire once one of these is sending webhooks.
//   { enabled: bool, isLoading: bool }
export function useBilling() {
  const { data, isLoading } = useOrgSettings();
  const stripe = !!data?.secrets_set?.['stripe.secret_key'];
  const samcart = !!data?.values?.['samcart.webhook_key_hash'];
  return { enabled: stripe || samcart, isLoading };
}

// Whether a CRM that posts activities back (LeadSquared) is connected: host,
// access key and secret all saved. Decides whether the "CRM activity logged"
// trigger is explained and offered - it can only fire once one is.
//   { enabled: bool, isLoading: bool }
export function useCrmConnected() {
  const { data, isLoading } = useOrgSettings();
  const v = data?.values || {};
  const enabled = !!(v['leadsquared.host'] && v['leadsquared.access_key'] && data?.secrets_set?.['leadsquared.secret_key']);
  return { enabled, isLoading };
}

// Whether the Academy module (Students, Cohorts, Finance, Expenses) is shown.
// Same soft-flag semantics as scheduling: absent / not-false => ON. Non-course
// businesses (lead-gen, appointments) turn this OFF to hide the whole section.
//   { enabled: bool, isLoading: bool }
export function useStudents() {
  const { data, isLoading } = useOrgSettings();
  const flag = data?.values?.['features.students'];
  return { enabled: flag !== false, isLoading };
}

// Whether this workspace is in E-COMMERCE mode: shows Orders / Customers /
// Purchase quality and hides the lead-gen furniture (pipeline, forms, calendar…).
// OPT-IN (default OFF): absent / not-true => OFF, so existing workspaces are
// unaffected. Set at signup (the "Online store" offering) or in Settings.
//   { enabled: bool, isLoading: bool }
export function useEcommerce() {
  const { data, isLoading } = useOrgSettings();
  return { enabled: data?.values?.['features.ecommerce'] === true, isLoading };
}

// Whether a STORE also works enquiries: restores the lead desk (pipeline,
// forms, chat, lead analytics) inside an e-commerce workspace.
//
// Only consulted when ecommerce is on - a lead-gen workspace has the desk
// already. OPT-IN (default OFF), so every existing store is untouched: a
// dealer-led store turns it on, a pure self-serve store never sees it.
//   { enabled: bool, isLoading: bool }
export function useLeadDesk() {
  const { data, isLoading } = useOrgSettings();
  return { enabled: data?.values?.['features.leads'] === true, isLoading };
}

// Whether DEALS are on: one row per SALE beside the lead that is one row per
// PERSON, so a customer who buys twice can be recorded twice.
//
// OPT-IN (default OFF). Every existing workspace keeps the product it has - the
// deals table simply stays empty, and an empty table reads as an empty board.
// Turning it on adds a screen; it does not change a single existing number.
//   { enabled: bool, isLoading: bool }
export function useDeals() {
  const { data, isLoading } = useOrgSettings();
  return { enabled: data?.values?.['features.deals'] === true, isLoading };
}

// Whether pooled / round-robin booking is available for booking forms. OPT-IN
// (default OFF): when on, a booking form can hand a single time slot to whichever
// rep in a pool is free (round-robin), instead of one fixed rep. Absent /
// not-true => OFF, so nothing changes for existing workspaces until they opt in.
//   { enabled: bool, isLoading: bool }
export function usePooledBooking() {
  const { data, isLoading } = useOrgSettings();
  const flag = data?.values?.['features.pooled_booking'];
  return { enabled: flag === true, isLoading };
}

// ─── Team ───────────────────────────────────────────────────────────────────
export function useTeam() {
  return useQuery({
    queryKey: ['team'],
    queryFn: async () => {
      const { data } = await api.get('/api/team');
      return Array.isArray(data) ? data : [];
    },
    staleTime: 30_000,
  });
}

export function useCreateTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => (await api.post('/api/team', body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  });
}

export function useUpdateTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }) => (await api.patch(`/api/team/${id}`, patch)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  });
}

export function useResetTeamMemberPassword() {
  return useMutation({
    mutationFn: async ({ id, password }) =>
      (await api.post(`/api/team/${id}/reset-password`, { password })).data,
  });
}

// Emails a teammate a fresh 7-day link to set their own password.
export function useInviteTeamMember() {
  return useMutation({
    mutationFn: async (id) => (await api.post(`/api/team/${id}/invite`)).data,
  });
}

export function useDeleteTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.delete(`/api/team/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['team'] }),
  });
}

// ─── Google Calendar OAuth status (current user) ───────────────────────────
export function useGoogleCalendarStatus() {
  return useQuery({
    queryKey: ['google-cal-status'],
    queryFn: async () => {
      const { data } = await api.get('/api/google-auth/status');
      return data; // { connected, email, token_expired }
    },
    staleTime: 30_000,
  });
}

export function useDisconnectGoogleCalendar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post('/api/google-auth/disconnect')).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['google-cal-status'] });
      qc.invalidateQueries({ queryKey: ['team'] });
    },
  });
}

// ─── Zoom (org-level connection) ────────────────────────────────────────────
export function useZoomStatus() {
  return useQuery({
    queryKey: ['zoom-status'],
    queryFn: async () => {
      const { data } = await api.get('/api/zoom-auth/status');
      return data; // { enabled, connected, account_email, connected_at }
    },
    staleTime: 15_000,
  });
}

export function useDisconnectZoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post('/api/zoom-auth/disconnect')).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['zoom-status'] }),
  });
}

// ─── Email senders + test send ────────────────────────────────────────────
export function useEmailSenders() {
  return useQuery({
    queryKey: ['email-senders'],
    queryFn: async () => {
      const { data } = await api.get('/api/settings/email/senders');
      return Array.isArray(data) ? data : [];
    },
    staleTime: 30_000,
  });
}

export function useSendTestEmail() {
  return useMutation({
    mutationFn: async ({ fromUserId, to }) =>
      (await api.post('/api/settings/email/test', { fromUserId, to })).data,
  });
}

// ─── Admin: get OAuth URL to connect Google for another team member ──────
export function useConnectGoogleForUser() {
  return useMutation({
    mutationFn: async (userId) =>
      (await api.post(`/api/google-auth/connect-for/${userId}`)).data,
  });
}

// ─── Razorpay test connection ─────────────────────────────────────────────
export function useTestRazorpay() {
  return useMutation({
    mutationFn: async () => (await api.post('/api/settings/razorpay/test')).data,
  });
}

// ─── Meta Lead Ads - validate saved Page token + list forms ───────────────
export function useTestMetaLeads() {
  return useMutation({
    mutationFn: async () => (await api.post('/api/settings/meta-leads/test')).data,
  });
}

// Exchange a System User token for the permanent Page token(s) via /me/accounts.
export function useResolveMetaPages() {
  return useMutation({
    mutationFn: async (system_user_token) =>
      (await api.post('/api/settings/meta-leads/resolve-pages', { system_user_token })).data,
  });
}

// Multi-page: the Pages currently connected to this workspace (id + name).
export function useMetaLeadsPages() {
  return useQuery({
    queryKey: ['meta-leads-pages'],
    queryFn: async () => (await api.get('/api/settings/meta-leads/pages')).data.pages || [],
    staleTime: 15_000,
  });
}
export function useSaveMetaLeadsPages() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (pages) => (await api.put('/api/settings/meta-leads/pages', { pages })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meta-leads-pages'] }),
  });
}
export function useRemoveMetaLeadsPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.delete(`/api/settings/meta-leads/pages/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meta-leads-pages'] }),
  });
}
// Passive token health - flags an expired/invalid Page token before leads drop.
export function useMetaLeadsHealth() {
  return useQuery({
    queryKey: ['meta-leads-health'],
    queryFn: async () => (await api.get('/api/settings/meta-leads/health')).data,
    staleTime: 60_000,
    retry: false,
  });
}
// One-click "Connect with Facebook" availability (+ a fresh OAuth URL).
export function useMetaConnect() {
  return useQuery({
    queryKey: ['meta-connect'],
    queryFn: async () => (await api.get('/api/settings/meta-leads/connect-url')).data,
    staleTime: 30_000,
    retry: false,
  });
}

// ─── WhatsApp Cloud API ──────────────────────────────────────────────────
export function useWhatsAppStatus() {
  return useQuery({
    queryKey: ['whatsapp-status'],
    queryFn: async () => (await api.get('/api/whatsapp/status')).data,
    staleTime: 15_000,
  });
}

export function useTestWhatsApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ to }) => (await api.post('/api/whatsapp/test', { to })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['whatsapp-status'] }),
  });
}

// The canonical templates merged with their live Meta approval status.
// NOTE: distinct queryKey from use-whatsapp-templates' useWhatsappTemplates() -
// that hook hits /api/whatsapp-templates and returns an ARRAY, this one hits
// /api/whatsapp/templates and returns an OBJECT ({ templates, config }). They
// MUST NOT share a cache key or the object poisons the array hook's cache and
// any `.filter/.find` on it crashes (the WhatsApp inbox did exactly that).
export function useWhatsAppTemplates() {
  return useQuery({
    queryKey: ['whatsapp-templates-status'],
    queryFn: async () => (await api.get('/api/whatsapp/templates')).data,
    staleTime: 15_000,
  });
}

// Submit one template (by name) or all not-yet-created ones to Meta for review.
// Pass force:true to re-push an edited body (deletes the old version first).
export function useSubmitWhatsAppTemplates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, force } = {}) =>
      (await api.post('/api/whatsapp/templates/submit', {
        ...(name ? { name } : {}),
        ...(force ? { force: true } : {}),
      })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['whatsapp-templates-status'] });
      qc.invalidateQueries({ queryKey: ['whatsapp-templates'] });
    },
  });
}

export function useWhatsAppThreads(archived = false) {
  return useQuery({
    queryKey: ['whatsapp-threads', archived ? 'archived' : 'active'],
    queryFn: async () => (await api.get('/api/whatsapp/threads', {
      params: archived ? { archived: 1 } : {},
    })).data,
    refetchInterval: 15_000,
    staleTime: 5_000,
  });
}

// Hide a chat from the inbox (archived:true) or restore it (archived:false).
export function useArchiveThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ leadId, archived }) =>
      (await api.patch(`/api/whatsapp/threads/${leadId}/archive`, { archived })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['whatsapp-threads'] }),
  });
}

// ─── Webhook endpoints (reused from /tracking/webhooks for the quick-look tab) ─
export function useWebhookEndpoints() {
  return useQuery({
    queryKey: ['webhook-endpoints'],
    queryFn: async () => {
      const { data } = await api.get('/api/webhooks');
      return Array.isArray(data) ? data : [];
    },
    staleTime: 30_000,
  });
}
